import React from 'react';
import { motion } from 'framer-motion';

// Mom character using actual image
export const MomCharacter = ({ className = '', animate = true, size = 'md' }) => {
  const sizes = {
    sm: { width: 80, height: 110 },
    md: { width: 140, height: 190 },
    lg: { width: 180, height: 240 },
    xl: { width: 220, height: 300 }
  };
  const { width, height } = sizes[size];
  
  const Wrapper = animate ? motion.div : 'div';
  const animateProps = animate ? {
    animate: { y: [0, -8, 0] },
    transition: { duration: 2.5, repeat: Infinity, ease: 'easeInOut' }
  } : {};

  return (
    <Wrapper className={className} {...animateProps}>
      <img 
        src="/assets/mom.png" 
        alt="Mama" 
        style={{ width, height, objectFit: 'contain' }}
        className="drop-shadow-lg"
      />
    </Wrapper>
  );
};

// Dad character using actual image
export const DadCharacter = ({ className = '', animate = true, size = 'md' }) => {
  const sizes = {
    sm: { width: 80, height: 110 },
    md: { width: 140, height: 190 },
    lg: { width: 180, height: 240 },
    xl: { width: 220, height: 300 }
  };
  const { width, height } = sizes[size];
  
  const Wrapper = animate ? motion.div : 'div';
  const animateProps = animate ? {
    animate: { y: [0, -10, 0] },
    transition: { duration: 2.8, repeat: Infinity, ease: 'easeInOut', delay: 0.3 }
  } : {};

  return (
    <Wrapper className={className} {...animateProps}>
      <img 
        src="/assets/dad.png" 
        alt="Papa" 
        style={{ width, height, objectFit: 'contain' }}
        className="drop-shadow-lg"
      />
    </Wrapper>
  );
};

// Both parents together (for story scenes)
export const ParentsTogether = ({ className = '', size = 'lg' }) => {
  return (
    <div className={`flex items-end justify-center gap-0 ${className}`}>
      <MomCharacter size={size} />
      <DadCharacter size={size} />
    </div>
  );
};

// Parents in a boat - parents BEHIND boat (as if sitting inside)
export const ParentsInBoat = ({ className = '', size = 'large' }) => {
  const isLarge = size === 'large';
  const parentWidth = isLarge ? 100 : 70;
  const parentHeight = isLarge ? 130 : 90;
  const boatWidth = isLarge ? 280 : 200;
  const boatHeight = isLarge ? 120 : 85;

  return (
    <motion.div 
      className={`relative ${className}`}
      animate={{ y: [0, -10, 0], rotate: [-1, 1, -1] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    >
      <div className="relative" style={{ width: boatWidth, height: boatHeight + 80 }}>
        {/* Parents BEHIND boat (lower z-index) - visible from waist up */}
        <div 
          className="absolute left-1/2 flex items-end justify-center gap-0"
          style={{ 
            transform: 'translateX(-50%)',
            top: 0,
            zIndex: 1
          }}
        >
          <img 
            src="/assets/mom.png" 
            alt="Mama" 
            style={{ width: parentWidth, height: parentHeight, objectFit: 'contain' }}
            className="drop-shadow-md"
          />
          <img 
            src="/assets/dad.png" 
            alt="Papa" 
            style={{ width: parentWidth, height: parentHeight, objectFit: 'contain' }}
            className="drop-shadow-md"
          />
        </div>
        
        {/* Boat SVG IN FRONT (higher z-index) */}
        <svg 
          width={boatWidth} 
          height={boatHeight} 
          viewBox="0 0 280 120" 
          fill="none" 
          className="absolute bottom-0 left-0"
          style={{ zIndex: 10 }}
        >
          {/* Boat hull */}
          <path
            d="M20 80 Q35 115, 140 115 Q245 115, 260 80 L230 50 L50 50 Z"
            fill="#8B4513"
            stroke="#5D3A1A"
            strokeWidth="3"
          />
          {/* Boat deck */}
          <rect x="45" y="45" width="190" height="12" rx="3" fill="#D97706" />
          {/* Boat interior (darker) */}
          <path
            d="M55 55 L55 75 Q140 85, 225 75 L225 55 Z"
            fill="#6B4423"
          />
          {/* Railing posts */}
          <rect x="60" y="35" width="4" height="15" rx="1" fill="#D97706" />
          <rect x="100" y="35" width="4" height="15" rx="1" fill="#D97706" />
          <rect x="176" y="35" width="4" height="15" rx="1" fill="#D97706" />
          <rect x="216" y="35" width="4" height="15" rx="1" fill="#D97706" />
          {/* Railing rope */}
          <path d="M62 38 Q140 32, 218 38" stroke="#A16207" strokeWidth="2" fill="none" />
        </svg>
        
        {/* Water splash around boat */}
        <div 
          className="absolute left-1/2 -translate-x-1/2"
          style={{ bottom: -5, zIndex: 11 }}
        >
          <svg width={boatWidth + 40} height="25" viewBox="0 0 320 25">
            <ellipse cx="160" cy="12" rx="155" ry="12" fill="rgba(56, 189, 248, 0.5)" />
          </svg>
        </div>
      </div>
    </motion.div>
  );
};

// Parents for celebration (final screen) - smaller version
export const ParentsCelebrating = ({ className = '' }) => {
  return (
    <motion.div 
      className={`relative ${className}`}
      animate={{ y: [0, -6, 0] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    >
      <div className="flex items-end justify-center gap-0">
        <motion.img 
          src="/assets/mom.png" 
          alt="Mama" 
          className="w-20 h-28 object-contain drop-shadow-md"
          animate={{ rotate: [-3, 3, -3] }}
          transition={{ duration: 1, repeat: Infinity }}
        />
        <motion.img 
          src="/assets/dad.png" 
          alt="Papa" 
          className="w-20 h-28 object-contain drop-shadow-md"
          animate={{ rotate: [3, -3, 3] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
        />
      </div>
    </motion.div>
  );
};

export default { MomCharacter, DadCharacter, ParentsTogether, ParentsInBoat, ParentsCelebrating };
